<template>
  <!-- eslint-disable max-len -->
  <edit-page
    ref="editPage"
    name="Registration Link"
    model-name="registrationLink"
    param="registrationLinkId"
    page-prefix="/blade/registration-links"
    :create="createRegistrationLink"
    :fetch="getRegistrationLink"
    :fetch-types="getUserTypes"
    :update="updateRegistrationLink"
    :remove="deleteRegistrationLink"
    :stub="registrationLink"
    :validator="validator"
    :utils="utils"
  >
    <template
      slot-scope="{ model: registrationLink, validator, utils: { unused } }"
    >
      <template
        v-if="
          !creating && registrationLink.link && registrationLink.link.length > 0
        "
      >
        <text-input
          v-if="!isCorpo"
          :disabled="true"
          label
          :value="getUrl(registrationLink)"
          copyable
          no-count
          >Registration Link</text-input
        >
        <button class="btn" @click.prevent="registrationLink.link = null">
          Reset Link (will regenerate on saving)
        </button>
      </template>

      <!-- metadata -->
      <text-input
        label
        v-model.trim="registrationLink.name"
        :validator="validator.name"
        required
        autofocus
        >Name</text-input
      >

      <checkbox-input
        v-model="registrationLink.uniqueIdSignin"
        :disabled="!canManageGames"
        >Use Unique ID Sign In</checkbox-input
      >
      <checkbox-input
        v-if="registrationLink.uniqueIdSignin"
        v-model="registrationLink.uniqueIdSigninAutoRegister"
        :disabled="!canManageGames"
        >Automatically Create Unique ID Accounts</checkbox-input
      >
      <checkbox-input
        v-if="registrationLink.uniqueIdSigninAutoRegister"
        v-model="registrationLink.uniqueIdSigninAutoGenerateCode"
        :disabled="!canManageGames"
        >Automatically Generate Unique ID (if none is provided)</checkbox-input
      >

      <div class="rate-limit-block">
        <h5>IP Rate Limiting Settings</h5>
        <div>
          The following settings allow you to limit the number of sign ups that
          can be made from a single IP address within a given time window. This
          is intended to prevent abuse of the registration link, particularly
          when anonymous registrations are enabled.  Note that, due to horizontal
          scaling, this is not an exact limit.

          <br/>
          <br/>
          <i>Note: If either setting is empty or 0, rate limiting is disabled.</i>
          <!-- Display for rate limit.  Displays "rate limit enabled: X sign ups allowed every {Y < 60 ? (Y seconds) : (Y / 60 minutes)}" -->
          <template
            v-if="
              registrationLink.ipBasedRateLimitCount > 0 &&
              registrationLink.ipBasedRateLimitWindowSeconds > 0
            "
          >
            <div class="rate-limit rate-limit-enabled">
              Rate Limit Enabled:
              {{ registrationLink.ipBasedRateLimitCount }} sign ups allowed
              every
              {{ rateLimitDisplayPeriod(registrationLink) }}
            </div>
          </template>
          <template v-else>
            <div class="rate-limit rate-limit-disabled">
              Rate Limit Disabled
            </div>
          </template>
        </div>

        <number-input
          label
          v-model.number="registrationLink.ipBasedRateLimitCount"
          :validator="{ required }"
          required
          >Rate Limit User Count</number-input
        >
        <number-input
          label
          v-model.number="registrationLink.ipBasedRateLimitWindowSeconds"
          :validator="{ required }"
          required
          >Rate Limit Window (seconds)</number-input
        >
      </div>

      <!-- Next Header -->
      <template v-if="registrationLink.uniqueIdSignin">
        <text-input label v-model.trim="registrationLink.uniqueIdLoginHeader"
          >Login Header Text</text-input
        >
        <text-input
          label
          v-model.trim="registrationLink.uniqueIdLoginPlaceholder"
          >Login Code Text</text-input
        >
      </template>
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        label
        v-model.trim="registrationLink.qualtricsRedirectURL"
        >Centiment Redirect URL (Game Complete URL)</text-input
      >
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        label
        v-model.trim="registrationLink.qualtricsExitURL"
        >Centiment Exit URL (I no longer want to take this survey
        URL)</text-input
      >

      <checkbox-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        v-model="registrationLink.disableCancelSurveyButton"
        >Disable Cancel Survey Button</checkbox-input
      >
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister &&
          !registrationLink.disableCancelSurveyButton
        "
        label
        v-model.trim="registrationLink.cancelSurveyButtonText"
        >Cancel Survey Button Text (replaces "I no longer want to take this
        survey" text)</text-input
      >

      <checkbox-input
        v-if="!registrationLink.uniqueIdSignin"
        v-model="registrationLink.skipEmailVerification"
        >Skip Email Verification</checkbox-input
      >
      <checkbox-input v-else :value="true" disabled
        >Skip Email Verification (automatically enabled when using unique ID
        sign in)</checkbox-input
      >

      <text-input
        label
        v-model.trim="registrationLink.emailDomain"
        :validator="validator.emailDomain"
        max-length
      >
        Associated Email Domain (all {{ studentEmployeeStringLowerPlural }} will
        be required to join the {{ classTypeName }} using an email with this
        domain; supports wildcards)
      </text-input>

      <checkbox-input v-model="registrationLink.enableRegistrationNotifications"
        >Enable Account Registration Notifications</checkbox-input
      >

      <template v-if="registrationLink.enableRegistrationNotifications">
        <text-input
          @input="emailListFlip = !emailListFlip"
          label
          v-model.trim="registrationLink.registrationEmailList"
          >Registration Notification Recipients</text-input
        >
        <div class="email-list-recipients">
          Notifications will be sent to {{ emailRecipientList }}
        </div>
      </template>
      <select-input
        label
        v-model="registrationLink.signInConfig"
        :options="signInConfigs"
        use="_id"
        :display="
          (e) => {
            return `${e.name}`;
          }
        "
        :to="
          (id) => {
            return `/blade/domainbrandings/${id}/edit`;
          }
        "
      >
        Domain Branding
      </select-input>

      <array-input
        :items="registrationLink.games"
        :hide-button="registrationLink.games.length >= games.length"
      >
        <h5 slot="title">
          Assigned Gamulation(s) (overrides Active flag gamulation for this
          User)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: game, index: i }"
          v-model="game._id"
          :options="
            unused(games).by('_id').in(registrationLink.games).by('_id').at(i)
          "
          use="_id"
          display="name"
          required
          autofocus
          :remove="
            () => {
              registrationLink.games.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/games/${id}/edit`;
            }
          "
        >
          Gamulation
        </select-input>
        <span slot="button">Add Gamulation</span>
      </array-input>

      <array-input
        :items="registrationLink.gameSets"
        :hide-button="
          registrationLink.gameSets &&
          registrationLink.gameSets.length >= gameSetList.length
        "
      >
        <h5 slot="title">
          Assigned Gamulation Set(s) (defines the sets of games that are
          visible/available to the registrationLink)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: gameSet, index: i }"
          v-model="gameSet._id"
          :options="
            unused(gameSetList)
              .by('_id')
              .in(registrationLink.gameSets)
              .by('_id')
              .at(i)
          "
          use="_id"
          display="name"
          required
          autofocus
          :remove="
            () => {
              registrationLink.gameSets.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/gamesets/${id}/edit`;
            }
          "
        >
          Gamulation Set
        </select-input>
        <span slot="button">Add Gamulation Set</span>
      </array-input>

      <array-input
        :items="registrationLink.classes"
        :hide-button="registrationLink.classes.length >= classes.length"
      >
        <h5 slot="title">
          Assigned {{ classTypeNamePlural }} (overrides everything when set)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: selectedClass, index: i }"
          class="col-sm-8"
          v-model="selectedClass._id"
          :options="
            unused(classes)
              .by('_id')
              .in(registrationLink.classes)
              .by('_id')
              .at(i)
          "
          use="_id"
          display="className"
          required
          autofocus
          :remove="
            () => {
              registrationLink.classes.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/classes/${id}/edit`;
            }
          "
        >
          {{ classTypeName }}
        </select-input>
        <span slot="button">Add {{ classTypeName }}</span>
      </array-input>

      <array-input
        :items="registrationLink.roles"
        :disabled="validator.roles.$invalid"
        :hide-button="
          !canAddRoles(registrationLink.roles) ||
          registrationLink.roles.length >=
            getUserRolesControlled(userRoles).length
        "
      >
        <h5 slot="title">Roles</h5>

        <select-input
          slot="item"
          slot-scope="{ item: role, index: i }"
          v-model="role._id"
          :disabled="
            !isOwnerOrBladeAdmin && `${me._id}` === `${registrationLink._id}`
          "
          :options="
            unused(getUserRolesControlled(userRoles))
              .by()
              .in(registrationLink.roles)
              .by('_id')
              .at(i)
          "
          required
          autofocus
          :remove="
            () => {
              registrationLink.roles.splice(i, 1);
            }
          "
        >
          Role
        </select-input>
        <span slot="button">Add User Role</span>
      </array-input>

      <collapsible-section
        name="Hub Label Overrides"
        :buttonStyle="{
          'font-size': '120%'
        }"
      >
        <text-input
          label
          no-count
          v-model.trim="registrationLink.hubClassesOverrideText"
          >Classes/Workgroups Override Text (if blank, will use the default)
        </text-input>
        <text-input
          label
          no-count
          v-model.trim="registrationLink.hubGamesOverrideText"
          >Gamulations Override Text (if blank, will use the default)
        </text-input>
      </collapsible-section>
    </template>
  </edit-page>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';

import ArrayInput from '../../ui/inputs/ArrayInput';
import EditPage from '../../ui/pages/EditPage';

import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import NumberInput from '../../ui/inputs/NumberInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import SignInConfigEditor from './components/SignInConfigEditor';
import { unused } from '../../../utils/utils';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';

function computeEmailListFromString(list) {
  if (!list || list.length === 0) { return ['notifications@redmerproductions.com']; }

  const separated = list.split(',');
  if (separated.length === 0) return ['notifications@redmerproductions.com'];
  return separated;
}

export default {
  components: {
    ArrayInput,
    EditPage,
    AssetSelectInput,
    SelectInput,
    TextInput,
    NumberInput,
    CheckboxInput,
    SignInConfigEditor,
    CollapsibleSection
  },
  data() {
    return {
      userRoles: [],
      gameSetList: [],
      classes: [],
      games: [],
      signInConfigs: [],

      arraysFlip: false,
      emailListFlip: false,

      utils: {
        unused,
      },

      registrationLink: {
        // metadata
        _id: '',
        name: '',
        signInConfig: '',

        gameSets: [],
        classes: [],
        games: [],
        roles: [{ _id: 'student' }],

        skipEmailVerification: false,
        enableRegistrationNotifications: false,
        registrationEmailList: '',

        uniqueIdSignin: false,
        uniqueIdLoginHeader: 'Login',
        uniqueIdLoginPlaceholder: 'Login Code',
        uniqueIdSigninAutoRegister: false,
        qualtricsRedirectURL: '',
        qualtricsExitURL: '',
        disableCancelSurveyButton: false,
        cancelSurveyButtonText: '',

        ipBasedRateLimitCount: 0,
        ipBasedRateLimitWindowSeconds: 0,

        hubClassesOverrideText: '',
        hubGamesOverrideText: '',

        updated: new Date(),
      },
      validator: {
        name: { required },
        emailDomain: { maxLength: maxLength(300) },
        roles: {
          $each: {
            required,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters([
      'isCorpo',
      'isInternalCorpo',
      'isOwnerOrBladeAdmin',
      'isNonBladeAdminContent',
      'activeSignInConfig',
      'classTypeNamePlural',
      'classTypeName',
      'canManageGames',
    ]),
    studentEmployeeStringLowerPlural() {
      return this.isInternalCorpo
        ? 'students/employees'
        : this.isCorpo
          ? 'employees'
          : 'students';
    },
    creating() {
      return !this.$route.params.registrationLinkId;
    },
    emailRecipientList() {
      if (this.emailListFlip && !this.emailListFlip) return;

      let list = computeEmailListFromString(
        (this.$refs.editPage.model.registrationEmailList || '').trim()
      );
      list = list.map(a => `"${a}"`);
      return list.join(', ');
    },
  },
  methods: {
    ...mapActions([
      'getUserTypes',
      'createRegistrationLink',
      'getRegistrationLink',
      'updateRegistrationLink',
      'deleteRegistrationLink',
      'getEnvironments',
      'getSignInConfigs',
      'getGameSets',
      'getClasses',
      'getGames',
    ]),
    rateLimitDisplayPeriod(registrationLink) {
      const seconds = registrationLink.ipBasedRateLimitWindowSeconds;
      if (seconds <= 60) {
        return `${seconds} seconds`;
      }
      return `${seconds / 60} minutes`;
    },
    getUrl(registrationLink) {
      if (registrationLink.link) {
        if (registrationLink.uniqueIdSignin) {
          return `${window.location.origin}/signin/link/${registrationLink.link}`;
        }
        return `${window.location.origin}/signup/link/${registrationLink.link}`;
      }
      return 'No link has been generated';
    },
    getUserRolesControlled(userRoles) {
      if (this.isOwnerOrBladeAdmin) {
        return userRoles;
      }
      if (this.isNonBladeAdminContent) {
        return userRoles.filter(
          r => r !== 'owner' && r !== 'developer' && r !== 'blade'
        );
      }
      return userRoles.filter(
        r =>
          r !== 'owner' &&
          r !== 'developer' &&
          r !== 'blade' &&
          r !== 'admin-content'
      );
    },
    canAddRoles(roles) {
      return this.isOwnerOrBladeAdmin || roles.length < 1;
    },
  },
  async mounted() {
    const classReq = this.getClasses();
    const signInConfigs = this.getSignInConfigs();
    const gameReq = this.getGames();
    const gameSetReq = this.getGameSets();
    const userRoleReq = this.getUserTypes();

    this.signInConfigs = await signInConfigs;
    this.classes = await classReq;
    this.games = await gameReq;
    this.gameSetList = await gameSetReq;
    this.userRoles = (await userRoleReq).userRoles;

    await this.$nextTick();
  },
};
</script>

<style lang="scss" scoped>
.form-image-background + span {
  display: block;
  margin: -10px 0 10px 10px;
}
.form-image-background {
  background-color: rgba(0, 0, 0, 0);
}
.role-override {
  border: 2px solid rgba(0, 0, 0, 0.4);
  padding: 10px;
  margin: 5px;
  margin-bottom: 10px;
}
.email-list-recipients {
  margin-top: 0px;
  margin-bottom: 10px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.5);
}

.rate-limit-block {
  border: 2px solid rgba(0, 0, 0, 0.4);
  padding: 10px;
}

.rate-limit {
  padding: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  border: 2px solid rgba(0, 0, 0, 0.4);
  font-style: italic;
  color: rgba(0, 0, 0, 0.5);
}

.rate-limit-enabled {
  background-color: rgba(0, 255, 0, 0.1);
}

.rate-limit-disabled {
  background-color: rgba(255, 0, 0, 0.1);
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/RegistrationLinkEdit.vue